/**
 * Turning the database's words into the reader's words.
 *
 * The API returns identifiers because identifiers are what the database stores and what the
 * audit trail has to preserve: `pmoac`, `mitigating_action`, `returned`. Every one of those
 * is correct and none of them belongs on a screen. A director reading `pmo_admin` in an
 * approval route is being shown a column value, not told who decides.
 *
 * Three closed sets and one open one, handled differently on purpose:
 *
 *   Roles      A closed set of six (`RoleCode` in apps/api/models/tenancy.py), named in the
 *              client's own workflow diagram. Worth translating properly, in both languages.
 *
 *   Decisions  A closed set of three. Same.
 *
 *   Fields     ~150 column keys across eight registers, and the client's own terminology.
 *              These are *not* translated — see the note in lib/registers.ts — but they are
 *              not shown raw either. `mitigating_action` becomes "Mitigating action": the
 *              same words the client chose, presented as a label instead of as a key. That
 *              is a formatting change, not a translation, so it cannot say the wrong thing.
 *
 * The identifier is always the fallback. A role or a decision the client adds after this
 * ships renders as itself rather than as a blank.
 */
import type { TFunction } from 'i18next'

export function roleLabel(t: TFunction, code: string | null | undefined): string {
  if (!code) return '—'
  return t(`roles.${code}`, { defaultValue: humanise(code) })
}

export function decisionLabel(t: TFunction, code: string | null | undefined): string {
  if (!code) return '—'
  return t(`decision.${code}`, { defaultValue: humanise(code) })
}

/**
 * The name of a close-readiness gate.
 *
 * There are five (`CloseGate` in apps/api/services/close.py) and the API sends their names
 * in English, because they are written where the rule is enforced. A closed set of five is
 * worth translating.
 *
 * The `detail` beside each one is not translated and cannot be from here: it is a sentence
 * the server composes with live counts — "498 of 517 packages have submitted progress" —
 * so an Arabic reader gets an Arabic gate name and an English explanation of it. That is
 * better than an English name and an English explanation, and it is not finished. Fixing it
 * properly means the API returning the counts as data instead of as prose.
 */
export function gateLabel(t: TFunction, id: string, fallback: string): string {
  return t(`gate.${id}`, { defaultValue: fallback })
}

/**
 * A register column's key as a label.
 *
 * Deliberately not translated and deliberately not looked up: the application payload names
 * the field but not the register it belongs to, so there is no catalogue to look the label
 * up in without an API change. Humanising the key gives the client's own words back, which
 * is honest; guessing at a translation of a term like "NOC reference" is not.
 */
export function fieldLabel(key: string): string {
  return humanise(key)
}

/** `mitigating_action` → `Mitigating action`. Acronyms the client uses stay upper case. */
const ACRONYMS = new Set(['id', 'noc', 'wbs', 'aed', 'pmo', 'kpi', 'boq', 'vo'])

function humanise(key: string): string {
  const words = key.split(/[_\-\s]+/).filter(Boolean)
  if (words.length === 0) return key
  return words
    .map((word, index) => {
      if (ACRONYMS.has(word.toLowerCase())) return word.toUpperCase()
      // Only the first word is capitalised: "Mitigating action", not "Mitigating Action".
      // Title Case reads as a heading, and these are labels beside values.
      if (index > 0) return word.toLowerCase()
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}
